import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Star, Clock, Award, CheckCircle2, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { courses } from '../mock/data';

const Courses = () => {
  return (
    <div className="min-h-screen bg-offwhite text-navy">
      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block">
              <Badge className="bg-navy text-offwhite border-0 px-4 py-1.5 text-sm mb-4">
                Premium Courses
              </Badge>
            </div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-navy mb-6">
              Our <span className="text-electric">Courses</span>
            </h1>
            <p className="text-xl text-navy/70 max-w-3xl mx-auto leading-relaxed">
              Comprehensive chemistry courses designed for JEE, NEET, and school students with expert guidance and proven results.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {courses.map((course, index) => (
              <Card
                key={course.id}
                className="bg-white border border-navy/10 overflow-hidden transition-colors duration-200 hover:border-navy/20"
              >
                  {/* Image Section */}
                  <div className="relative overflow-hidden">
                    <img
                      src={course.image}
                      alt={course.title}
                      className="w-full h-52 object-cover"
                    />
                    <div className="absolute top-3 right-3 flex flex-wrap justify-end gap-2 max-w-[calc(100%-1.5rem)]">
                      <Badge className="bg-electric text-offwhite border-0">
                        {course.category}
                      </Badge>
                      <Badge className={`backdrop-blur-sm text-white border-0 ${
                        course.level === 'Advanced' ? 'bg-teal' : 'bg-navy'
                      }`}>
                        {course.level}
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-bold text-offwhite mb-2">
                        {course.title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-offwhite/90">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{course.duration}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                    <div className="p-6">
                    <p className="text-navy/70 mb-4 leading-relaxed">
                      {course.description}
                    </p>

                    <div className="flex items-center justify-between mb-6 pb-6 border-b border-navy/10">
                      <div className="flex items-center space-x-2 text-navy/70">
                        <Users className="w-5 h-5 text-electric" />
                        <span className="font-medium">{course.students}+ Students</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                        <span className="text-navy font-medium">{course.rating}</span>
                      </div>
                      {/* Pricing removed */}
                    </div>

                    {/* Features */}
                    <div>
                      <h4 className="text-lg font-semibold text-navy mb-3 flex items-center">
                        <Award className="w-5 h-5 mr-2 text-electric" />
                        Course Features
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {course.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start space-x-2 text-sm text-navy/70">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 text-teal flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Highlights */}
                    <div className="mt-4">
                      <h4 className="text-lg font-semibold text-navy mb-3">Course Highlights</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {course.highlights.map((highlight, idx) => (
                          <div key={idx} className="bg-offwhite-dark border border-navy/10 rounded-lg p-3">
                            <p className="text-navy text-sm font-medium">{highlight}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                      <Link to={`/courses/${course.id}`} className="flex-1">
                        <Button className="w-full bg-electric hover:bg-electric-dark text-offwhite py-6 rounded-xl">
                          View Details <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </Link>
                      <a
                        href="https://api.whatsapp.com/send?phone=7595888451"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button className="w-full bg-teal hover:bg-teal-dark text-offwhite py-6 rounded-xl">
                          Enroll Now <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </a>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-4">
              Why Choose <span className="text-gradient">KCC</span>?
            </h2>
            <p className="text-xl text-navy/70">
              Experience the difference with our proven teaching methodology
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: 'Expert Faculty', desc: '18 years teaching experience' },
              { icon: Users, title: '2000+ / 400+ Aspirants', desc: 'Engineering / Medical mentored' },
              { icon: Star, title: '95% Success Rate', desc: 'In competitive exams' },
              { icon: CheckCircle2, title: 'Proven Results', desc: 'Top rankers every year' }
            ].map((item, idx) => (
              <Card
                key={idx}
                className="bg-white border border-navy/10 p-6 text-center transition-colors duration-200 hover:border-navy/20"
              >
                <div className="w-12 h-12 rounded-lg bg-offwhite-dark flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-electric" />
                </div>
                <h3 className="text-xl font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-navy/70 text-sm">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-navy border border-navy/10 p-8 md:p-12 text-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-offwhite mb-4">
                Ready to Start Your Journey?
              </h2>
              <p className="text-offwhite/80 mb-8 text-lg">
                Join thousands of successful students and achieve your dream rank
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://api.whatsapp.com/send?phone=7595888451"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-electric hover:bg-electric-dark text-offwhite px-8 py-6 text-lg rounded-full">
                    Enroll Now <ArrowRight className="ml-2" />
                  </Button>
                </a>
                <Link to="/mentors">
                  <Button variant="outline" className="border-2 border-offwhite/40 text-offwhite hover:bg-offwhite/10 px-8 py-6 text-lg rounded-full">
                    Meet Our Mentors
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Courses;
